/**
 * Panel Plugin Registry
 *
 * Central registry for side-panel visualization plugins (map, diagrams, etc.).
 * Each plugin self-registers via `registerPanel()` and the ChatBody orchestrator
 * discovers them via `getPanel()` without importing concrete implementations.
 *
 * Plugin contract — every registered object must expose:
 *   type       : string              — unique key (e.g. 'map')
 *   icon       : Preact component    — badge icon rendered next to the message
 *   label      : (t) => string       — badge label text (receives the i18n helper)
 *   mount(container, data, ctx): { destroy(), update(data) }
 *                                    — imperative lifecycle for the panel content
 *   isEnabled? : (config) => boolean — optional gate; defaults to true
 *   onOpen?    : (shadowRoot) => void — optional setup hook (e.g. inject CSS)
 */

const registry = new Map();

/**
 * Registers a panel plugin. Duplicate type keys overwrite the previous entry.
 *
 * @param {Object} plugin - The panel plugin descriptor.
 */
export function registerPanel(plugin) {
  if (!plugin || !plugin.type) {
    throw new Error('Panel plugin must have a "type" property.');
  }
  registry.set(plugin.type, plugin);
}

/**
 * Retrieves a registered panel plugin by its unique type key.
 *
 * @param {string} type - The panel type identifier.
 * @returns {Object|undefined} The plugin descriptor, or undefined if not registered.
 */
export function getPanel(type) {
  return registry.get(type);
}

/**
 * Checks whether a panel plugin is registered for the given type.
 *
 * @param {string} type - The panel type identifier.
 * @returns {boolean}
 */
export function hasPanel(type) {
  return registry.has(type);
}
